import { Building2, Calendar, GraduationCap, MapPin, Phone, Star, Tag, Plus, Edit, Check, X, Loader2, Mail } from 'lucide-react'
import React, { useState, useContext, useEffect, useRef } from 'react'
import { FaLinkedin } from 'react-icons/fa'
import { toast } from 'react-toastify'
import ProcessStageDisplay from '../process/ProcessStageDisplay'
import ProcessBizDevStageDisplay from '../process/ProcessBizDevStageDisplay'
import AddToProcess from '../process/AddToProcess'
import { ApiContext } from '../../App'
import tagList from '../../utils/tagList'
import EmailListEditor from './EmailListEditor'
import { normalizeEmailsForBackend } from '../../utils/emailHelpers'

// Composant pour les champs éditables inline
const EditableField = ({ value, onSave, type = 'text', placeholder, icon: Icon, multiline = false, tel = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setCurrentValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' || type === 'url') {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  const handleSave = async () => {
    if (currentValue === (value || '')) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(currentValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving field:', error);
      setCurrentValue(value || '');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setCurrentValue(value || '');
      setIsEditing(false);
    }
  };

  const handleClick = () => {
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const displayValue = tel && value ? (
    <a href={`tel:${value}`} className="text-sm text-gray-700 hover:text-blue-400 transition-colors">
      {value}
    </a>
  ) : type === 'url' && value ? (
    <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm break-all text-gray-700 hover:text-blue-400 transition-colors">
      Voir le profil
    </a>
  ) : (
    <p className={`text-sm text-gray-700 ${multiline ? 'whitespace-pre-wrap' : ''}`}>
      {value || <span className="text-gray-400 italic">—</span>}
    </p>
  );

  return (
    <div className="flex items-start gap-2 group relative">
      {Icon && <Icon size={16} className="text-gray-700 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="space-y-1">
            {multiline ? (
              <textarea
                ref={inputRef}
                value={currentValue}
                onChange={(e) => setCurrentValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                rows={4}
                disabled={isSaving}
                className="w-full border p-2 rounded text-sm focus:ring-2 border-gray-300 focus:ring-indigo-500 resize-vertical"
              />
            ) : (
              <input
                ref={inputRef}
                type={type}
                value={currentValue}
                onChange={(e) => setCurrentValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={isSaving}
                className="w-full border p-2 rounded text-sm focus:ring-2 border-gray-300 focus:ring-indigo-500"
              />
            )}
            {isSaving && (
              <div className="flex items-center gap-2 text-xs text-indigo-600">
                <Loader2 size={12} className="animate-spin" />
                <span>Enregistrement...</span>
              </div>
            )}
          </div>
        ) : (
          <div
            onClick={handleClick}
            className="cursor-pointer hover:bg-gray-50 rounded p-2 -m-2 transition-colors"
            title="Cliquer pour modifier"
          >
            {displayValue}
            <div className="absolute right-0 top-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <Edit size={12} className="text-gray-400" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoTab = ( {individual, onIndividualUpdate, onStageUpdate }) => {
  const API = useContext(ApiContext);
  const [formData, setFormData] = useState({});
  const [newTag, setNewTag] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [processRefreshKey, setProcessRefreshKey] = useState(0);
  const [savingAnimation, setSavingAnimation] = useState(false);

  // Initialiser formData quand individual change
  useEffect(() => {
    if (individual) {
      setFormData(individual);
    }
  }, [individual]);

  
/*   // Validation des emails selon leur type
  const validateEmailForField = (email, fieldName) => {
    if (!email || email.trim() === '') return true;

    const isBusinessEmail = (email) => {
      const personalDomains = [
        "aol.com",
        "bouyguestelecom.fr",
        "fastmail.com",
        "free.fr",
        "gmail.com",
        "gmx.com",
        "hotmail.com",
        "hotmail.fr",
        "icloud.com",
        "laposte.net",
        "live.com",
        "live.fr",
        "mac.com",
        "mail.ru",
        "me.com",
        "msn.com",
        "orange.fr",
        "outlook.com",
        "protonmail.com",
        "sfr.fr",
        "t-online.de",
        "tutanota.com",
        "wanadoo.fr",
        "web.de",
        "yahoo.com",
        "yandex.com",
        "zoho.com"
      ];

      const domain = email.toLowerCase().split('@')[1];
      return domain && !personalDomains.includes(domain);
    };

    if (fieldName === 'professionalEmail') {
      return isBusinessEmail(email);
    } else if (fieldName === 'personalEmail') {
      return !isBusinessEmail(email);
    }
    return true;
  }; */

  // Fonction de sauvegarde pour un champ individuel
  const saveField = async (fieldName, value) => {
    setSavingAnimation(true);

    try {
      const updateData = { [fieldName]: value || null };

      if (fieldName === 'linkedinUrl' && (!value || value.trim() === '')) {
        updateData.linkedinUrl = null;
      }

      const response = await API.put(`/api/individuals/${individual._id}`, updateData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data) {
        if (onIndividualUpdate) {
          onIndividualUpdate(response.data);
        }
      }

      // Animation de succès
      setTimeout(() => {
        setSavingAnimation(false);
      }, 800);

    } catch (error) {
      console.error("Error updating field:", error);
      toast.error(error.response?.data?.error || "Erreur lors de la mise à jour");
      setSavingAnimation(false);
      throw error;
    }
  };

  // Fonction de sauvegarde pour les emails (arrays)
  const saveEmails = async (fieldName, emails) => {
    setSavingAnimation(true);

    try {
      const normalizedEmails = normalizeEmailsForBackend(emails);
      const updateData = { [fieldName]: normalizedEmails };

      const response = await API.put(`/api/individuals/${individual._id}`, updateData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data) {
        if (onIndividualUpdate) {
          onIndividualUpdate(response.data);
        }
      }

      setTimeout(() => {
        setSavingAnimation(false);
      }, 800);

    } catch (error) {
      console.error("Error updating emails:", error);
      toast.error(error.response?.data?.error || "Erreur lors de la mise à jour");
      setSavingAnimation(false);
      throw error;
    }
  };

  // Fonction pour mettre à jour le score
  const handleScoreChange = async (newScore) => {
    try {
      const scoreToSet = individual?.score === newScore ? 0 : newScore;

      const response = await API.put(`/api/individuals/${individual._id}/score`, { score: scoreToSet });
      if (response.data.success) {
        const updatedIndividual = { ...individual, score: scoreToSet };

        if (onIndividualUpdate) {
          onIndividualUpdate(updatedIndividual);
        }
      }
    } catch (error) {
      console.error('Error updating score:', error);
      toast.error('Erreur lors de la mise à jour du score');
    }
  };

  // Fonction pour ajouter un tag
  const addTagToIndividual = async () => {
    if (!newTag.trim()) return;
    const lowercaseTag = newTag.toLowerCase();

    try {
      const response = await API.put(
        `/api/individuals/${individual._id}/add-tag`,
        { tagName: lowercaseTag }
      );

      if (response.data.success && response.data.individual) {
        if (onIndividualUpdate) {
          onIndividualUpdate(response.data.individual);
        }
      }

      setNewTag('');
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Erreur lors de l\'ajout du tag');
    }
  };

  // Fonction pour supprimer un tag
  const removeTagFromIndividual = async (tagName) => {
    try {
      const response = await API.put(
        `/api/individuals/${individual._id}/remove-tag`,
        { tagName }
      );

      if (response.data.success && response.data.individual) {
        if (onIndividualUpdate) {
          onIndividualUpdate(response.data.individual);
        }
      }
    } catch (error) {
      console.error('Error removing tag:', error);
      toast.error('Erreur lors de la suppression du tag');
    }
  };

  // Fonction pour analyser et suggérer des tags
  const analyzeTags = async () => {
    setLoadingSuggestions(true);
    try {
      const textToAnalyze = [
        individual.Name || "",
        individual.jobTitle || "",
        individual.description || "",
        individual.city || "",
        individual.company || "",
        individual.formation || "",
        ...(individual.experiences || []).map(
          (exp) => `${exp.name || ""} ${exp.description || ""} ${exp.company || ""}`
        ),
        ...(individual.education || []).map(
          (edu) => `${edu.name || ""} ${edu.institution || ""} ${edu.fieldOfStudy || ""}`
        ),
      ].join(" ").toLowerCase();

      const matchedKeywords = [];
      tagList.forEach((keyword) => {
        const keywordLower = keyword.toLowerCase();
        const regex = new RegExp(`\\b${keywordLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        if (regex.test(textToAnalyze)) {
          matchedKeywords.push(keyword);
        }
      });

      const existingTagNames = individual.tags?.map(tag => tag.name) || [];

      const response = await API.post('/api/individuals/analyze-tags', {
        tagKeywords: matchedKeywords,
        existingTagNames,
      });

      if (response.data.success) {
        setSuggestedTags(response.data.suggestions);
        setShowSuggestions(true);

        if (response.data.suggestions.length === 0) {
          toast.info('Aucune suggestion de tag trouvée pour ce profil');
        }
      }
    } catch (error) {
      console.error('Error analyzing tags:', error);
      toast.error('Erreur lors de l\'analyse des tags');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Fonction pour ajouter un tag suggéré
  const addSuggestedTag = async (suggestedTag) => {
    try {
      const response = await API.put(
        `/api/individuals/${individual._id}/add-tag`,
        { tagName: suggestedTag.name.toLowerCase() }
      );

      if (response.data.success && response.data.individual) {
        if (onIndividualUpdate) {
          onIndividualUpdate(response.data.individual);
        }
      }

      setSuggestedTags(prev => prev.filter(t => t.name !== suggestedTag.name));

    } catch (error) {
      console.error('Error adding suggested tag:', error);
      toast.error('Erreur lors de l\'ajout du tag');
    }
  };

  return (
    <div>
      {/* Indicateur d'enregistrement global */}
      {savingAnimation && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-slide-in-right">
          <Check size={16} />
          <span className="text-sm font-medium">Enregistré</span>
        </div>
      )}

      {/* Content - Info complémentaires */}
      <div className="overflow-y-auto space-y-4">

        {/* Nom et Titre du poste - Toujours visible et éditable */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">
            Informations principales
          </h3>
          <div className="bg-white rounded-lg p-3 space-y-3 border border-gray-700">
            <div className="space-y-1">
              <label className="text-xs text-gray-700 font-medium">Nom complet</label>
              <EditableField
                value={individual.Name}
                onSave={(value) => saveField('Name', value)}
                placeholder="Nom complet"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-700 font-medium">Titre du poste</label>
              <EditableField
                value={individual.jobTitle}
                onSave={(value) => saveField('jobTitle', value)}
                placeholder="Titre du poste"
              />
            </div>
          </div>
        </section>

        {/* Modal AddToProcess */}
        {individual && individual._id && (
          <div className="">
            <AddToProcess
              individualId={individual._id}
              onStageUpdate={() => {
                setProcessRefreshKey(prev => prev + 1);
                if (onStageUpdate) onStageUpdate();
              }}
            />
          </div>
        )}

        {/* Processus de recrutement */}
        {individual && individual._id && (
          <ProcessStageDisplay
            key={`recruitment-${processRefreshKey}`}
            individualId={individual._id}
            onStageUpdate={() => {
              setProcessRefreshKey(prev => prev + 1);
              if (onStageUpdate) onStageUpdate();
            }}
          />
        )}

        {/* Processus BizDev */}
        {individual && individual._id && (
          <ProcessBizDevStageDisplay
            key={`bizdev-${processRefreshKey}`}
            individualId={individual._id}
            onStageUpdate={() => {
              setProcessRefreshKey(prev => prev + 1);
              if (onStageUpdate) onStageUpdate();
            }}
          />
        )}

        {/* Score (rating) - Modifiable */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">
            Évaluation
          </h3>

          <div className="bg-white rounded-lg p-3 border border-gray-700">
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleScoreChange(star)}
                  className="focus:outline-none hover:scale-110 transition-transform"
                  title={`Noter ${star}/5`}
                >
                  <Star
                    size={20}
                    className={`cursor-pointer ${star <= (individual?.score || 0)
                      ? "fill-yellow-400 text-yellow-400"
                      : "text-gray-400 hover:text-gray-500"
                    }`}
                  />
                </button>
              ))}
              <span className="text-sm text-gray-700 ml-2">
                {individual?.score || 0}/5
              </span>
            </div>
          </div>
        </section>

        {/* Tags */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">
            Tags
          </h3>

          <div className="bg-white rounded-lg p-3 border border-gray-700 space-y-3">
            {/* Tags existants */}
            <div className="flex flex-wrap gap-2">
              {individual.tags && individual.tags.length > 0 ? (
                individual.tags.map((tag) => (
                  <div
                    key={tag._id}
                    className="group relative"
                  >
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-300">
                      <Tag size={12} />
                      {tag.name}
                      <button
                        onClick={() => removeTagFromIndividual(tag.name)}
                        className="ml-1 text-red-500 hover:text-red-700 transition-colors"
                        title="Supprimer le tag"
                      >
                        <X size={12} />
                      </button>
                    </span>

                    {tag.explication && (
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white text-gray-700 text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap max-w-xs z-10 border border-gray-700 shadow-lg">
                        {tag.explication}
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                          <div className="border-4 border-transparent border-t-gray-700"></div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">Aucun tag</p>
              )}
            </div>

            {/* Ajouter un nouveau tag */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTagToIndividual();
                  }
                }}
                placeholder="Ajouter un tag..."
                className="border border-gray-300 px-3 py-1 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={addTagToIndividual}
                className="flex items-center gap-1 px-3 py-1 bg-indigo-500 text-white text-xs rounded hover:bg-indigo-600 transition-colors"
              >
                <Plus size={14} />
                Ajouter
              </button>
              <button
                onClick={analyzeTags}
                disabled={loadingSuggestions}
                className="flex items-center gap-2 px-3 py-1.5 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed justify-center"
              >
                <Tag size={14} />
                {loadingSuggestions ? 'Analyse en cours...' : 'Suggestions de tags'}
              </button>
            </div>

            {/* Liste des suggestions */}
            {showSuggestions && suggestedTags.length > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-700">
                    Tags suggérés ({suggestedTags.length})
                  </p>
                  <button
                    onClick={() => setShowSuggestions(false)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestedTags.map((suggestedTag, index) => (
                    <button
                      key={suggestedTag._id || `new-${index}`}
                      onClick={() => addSuggestedTag(suggestedTag)}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full border border-purple-200 hover:bg-purple-100 transition-colors cursor-pointer"
                      title={suggestedTag.explication || 'Cliquez pour ajouter ce tag'}
                    >
                      <Plus size={10} />
                      {suggestedTag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message si aucune suggestion */}
            {showSuggestions && suggestedTags.length === 0 && (
              <div className="pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 italic">
                  Aucune suggestion de tag trouvée pour ce profil.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Contact */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">
            Contact
          </h3>

          <div className="bg-white rounded-lg p-3 space-y-3 border border-gray-700">

            {/* Email professionnel */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Mail size={16} className="text-blue-400" />
                <p className="text-xs text-gray-700">Email professionnel</p>
              </div>
              <EmailListEditor
                emails={formData.professionalEmail}
                onChange={(newEmails) => {
                  setFormData(prev => ({ ...prev, professionalEmail: newEmails }));
                  saveEmails('professionalEmail', newEmails);
                }}
                placeholder="Ajouter un email professionnel..."
                isEditing={true}
             /* validateEmail={(email) => {
                  const isValid = validateEmailForField(email, 'professionalEmail');
                  if (!isValid) {
                    toast.error("Cet email semble être personnel. Utilisez le champ 'Email perso'.");
                  }
                  return isValid;
                }} */
              />
            </div>

            {/* Email personnel */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Mail size={16} className="text-green-400" />
                <p className="text-xs text-gray-700">Email personnel</p>
              </div>
              <EmailListEditor
                emails={formData.personalEmail}
                onChange={(newEmails) => {
                  setFormData(prev => ({ ...prev, personalEmail: newEmails }));
                  saveEmails('personalEmail', newEmails);
                }}
                placeholder="Ajouter un email personnel..."
                isEditing={true}
                /* validateEmail={(email) => {
                  const isValid = validateEmailForField(email, 'personalEmail');
                  if (!isValid) {
                    toast.error("Cet email semble être professionnel. Utilisez le champ 'Email pro'.");
                  }
                  return isValid;
                }} */
              />
            </div>

            {/* Téléphone */}
            <div className="space-y-1">
              <p className="text-xs text-gray-700">Téléphone</p>
              <EditableField
                value={individual.phoneNumber}
                onSave={(value) => saveField('phoneNumber', value)}
                type="tel"
                placeholder="Numéro de téléphone"
                icon={Phone}
                tel={true}
              />
            </div>

            {/* Ville */}
            <div className="space-y-1">
              <p className="text-xs text-gray-700">Ville</p>
              <EditableField
                value={individual.city}
                onSave={(value) => saveField('city', value)}
                placeholder="Ville"
                icon={MapPin}
              />
            </div>

            {/* LinkedIn */}
            <div className="space-y-1">
              <p className="text-xs text-gray-700">LinkedIn</p>
              <EditableField
                value={individual.linkedinUrl}
                onSave={(value) => saveField('linkedinUrl', value)}
                type="url"
                placeholder="URL LinkedIn"
                icon={FaLinkedin}
              />
            </div>
          </div>
        </section>

        {/* Informations professionnelles */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">
            Informations professionnelles
          </h3>

          <div className="bg-white rounded-lg p-3 space-y-3 border border-gray-700">
            {/* Entreprise */}
            <div className="space-y-1">
              <p className="text-xs text-gray-700">Entreprise</p>
              <EditableField
                value={individual.company}
                onSave={(value) => saveField('company', value)}
                placeholder="Entreprise"
                icon={Building2}
              />
            </div>

            {/* Formation */}
            <div className="space-y-1">
              <p className="text-xs text-gray-700">Formation</p>
              <EditableField
                value={individual.formation}
                onSave={(value) => saveField('formation', value)}
                placeholder="Formation"
                icon={GraduationCap}
              />
            </div>

            {/* Salaire actuel */}
            <div className="space-y-1">
              <p className="text-xs text-gray-700">Salaire actuel (k€)</p>
              <EditableField
                value={individual.SA}
                onSave={(value) => saveField('SA', value ? Number(value) : null)}
                type="number"
                placeholder="Salaire actuel (k€)"
              />
            </div>

            {/* Salaire espéré */}
            <div className="space-y-1">
              <p className="text-xs text-gray-700">Salaire espéré (k€)</p>
              <EditableField
                value={individual.SE}
                onSave={(value) => saveField('SE', value ? Number(value) : null)}
                type="number"
                placeholder="Salaire espéré (k€)"
              />
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">
            Description
          </h3>

          <div className="bg-white rounded-lg p-3 border border-gray-700">
            <EditableField
              value={individual.description}
              onSave={(value) => saveField('description', value)}
              placeholder="Description"
              multiline={true}
            />
          </div>
        </section>

        {/* Expériences professionnelles */}
        {individual.experiences && individual.experiences.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700 uppercase">
              Expériences ({individual.experiences.length})
            </h3>

            <div className="relative">
              {individual.experiences
                .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
                .map((exp, index) => (
                <div key={exp._id || index} className="relative pl-8 pb-8 last:pb-0">
                  {/* Ligne verticale */}
                  {index < individual.experiences.length - 1 && (
                    <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-blue-300"></div>
                  )}

                  {/* Point/cercle sur la timeline */}
                  <div className="absolute left-1.5 top-3 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow-md"></div>

                  {/* Contenu de l'expérience */}
                  <div className="bg-white rounded-lg p-3 border border-gray-300 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="mb-0 font-semibold text-sm text-gray-700">{exp.name}</h4>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                        {exp.contractType.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-blue-600 mb-1 font-medium">{exp.company}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                      <Calendar size={12} />
                      {new Date(exp.startDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })} -
                      {exp.isCurrentJob ? ' Présent' : (exp.endDate ? ` ${new Date(exp.endDate).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}` : ' Non spécifié')}
                    </div>
                    {exp.city && (
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <MapPin size={12} />
                        {exp.city}
                      </div>
                    )}
                    {exp.description && (
                      <p className="mt-2 text-xs text-gray-600">{exp.description}</p>
                    )}
                  </div>
                </div>

              ))}
            </div>
          </section>
        )}
      </div>

      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  )
}

export default InfoTab
